import { Copy, Download, FileText, RefreshCcw, Trash2, Wand2 } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

import { MediaFile } from '@/types/media'
import { formatSize } from '@/utils/formatSize'

interface FileDetailsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  file: MediaFile | null
  onCopyUrl: (url: string) => void
  onDelete: (id: string) => void
  onOptimize?: (file: MediaFile) => void
  onConvert?: (file: MediaFile) => void
}

export const FileDetailsDialog = ({
  open,
  onOpenChange,
  file,
  onCopyUrl,
  onDelete,
  onOptimize,
  onConvert,
}: FileDetailsDialogProps) => {
  if (!file) return null



  const renderPreview = () => {
    if (file.mimeType === 'application/pdf' || file.type === 'pdf') {
      return (
        <div className="relative h-[600px] w-full overflow-hidden rounded-lg border border-border-subtle bg-subdued">
          <iframe src={file.url} className="w-full h-full" title={file.name} />
        </div>
      )
    }

    const getYoutubeEmbed = (url: string) => {
      try {
        let videoId = '';
        // Basic parser
        if (url.includes('youtube.com/watch')) {
          const params = new URLSearchParams(new URL(url).search);
          videoId = params.get('v') || '';
        } else if (url.includes('youtu.be/')) {
          videoId = url.split('youtu.be/')[1]?.split('?')[0] || '';
        } else if (url.includes('youtube.com/embed/')) {
          return url;
        }
        return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
      } catch (e) { return url; }
    }

    const getFacebookEmbed = (url: string) => {
      return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=0`;
    }

    const isYoutube = (url: string) => url.match(/(youtube\.com|youtu\.be)/);
    const isFacebook = (url: string) => url.match(/(facebook\.com)/);

    let effectiveType = file.type;
    if (effectiveType === 'video') {
      if (isYoutube(file.url)) effectiveType = 'youtube_video';
      else if (isFacebook(file.url)) effectiveType = 'facebook_video';
    }

    switch (effectiveType) {
      case 'image':
        return (
          <div className="group relative flex aspect-video items-center justify-center overflow-hidden rounded-lg border border-border-subtle bg-subdued">
            <img src={file.url} alt={file.name} className="w-full h-full object-contain" />
          </div>
        )
      case 'youtube_video':
        return (
          <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
            <iframe
              src={getYoutubeEmbed(file.url)}
              className="w-full h-full"
              title={file.name}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
        )
      case 'facebook_video':
        return (
          <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
            <iframe
              src={getFacebookEmbed(file.url)}
              className="w-full h-full"
              title={file.name}
              allowFullScreen
              allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
              style={{ border: 'none', overflow: 'hidden' }}
            />
          </div>
        )
      case 'video':
        return (
          <div className="relative aspect-video bg-black rounded-lg overflow-hidden flex items-center justify-center group">
            <video
              src={file.url}
              controls
              className="w-full h-full"
              crossOrigin="anonymous"
              playsInline
            >
              Your browser does not support the video tag.
            </video>
          </div>
        )
      default:
        return (
          <div className="flex aspect-video flex-col items-center justify-center gap-3 rounded-lg border border-border-subtle bg-subdued">
            <FileText className="size-12 text-muted-foreground" />
            <p className="text-sm text-text-2">No preview available for this file type</p>
            <Button variant="outline" onClick={() => window.open(file.url, '_blank')}>
              Open in new tab
            </Button>
          </div>
        )
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size="lg"
        className={file.mimeType === 'application/pdf' ? 'flex h-[80vh] flex-col' : undefined}
      >
        <DialogHeader>
          <DialogTitle>File details</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {renderPreview()}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="min-w-0 space-y-2">
              <div>
                <h3 className="truncate text-heading-md text-ink" title={file.name}>
                  {file.name}
                </h3>
                <p className="truncate text-sm text-text-2">{file.filename}</p>
              </div>

              <div className="flex flex-wrap gap-2">
                <Badge tone="outline" className="capitalize">
                  {file.type}
                </Badge>
                <Badge tone="soft">
                  {file.mimeType}
                </Badge>
              </div>
            </div>

            <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm">
              <dt className="text-text-2">Size</dt>
              <dd className="tabular-nums text-ink">{formatSize(file.size)}</dd>

              {file.dimensions && (
                <>
                  <dt className="text-text-2">Resolution</dt>
                  <dd className="tabular-nums text-ink">{file.dimensions.width} × {file.dimensions.height} px</dd>
                </>
              )}

              <dt className="text-text-2">Uploaded by</dt>
              <dd className="truncate text-ink">{file.uploadedBy}</dd>

              <dt className="text-text-2">Date</dt>
              <dd className="text-ink">{new Date(file.createdAt).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}</dd>
            </dl>
          </div>
        </div>

        <DialogFooter>
          <div className="flex w-full justify-between gap-2 sm:mr-auto sm:w-auto sm:justify-start">
            <Button
              variant="destructive"
              size="icon"
              aria-label="Delete file"
              onClick={() => {
                onDelete(file.id)
                onOpenChange(false)
              }}
            >
              <Trash2 />
            </Button>
            <div className="flex gap-2">
              {file.type === 'image' && onOptimize && (
                <Button
                  variant="outline"
                  onClick={() => {
                    onOptimize(file)
                    onOpenChange(false)
                  }}
                >
                  <Wand2 />
                  Optimize
                </Button>
              )}
              {onConvert && (
                <Button
                  variant="outline"
                  onClick={() => {
                    onConvert(file)
                    onOpenChange(false)
                  }}
                >
                  <RefreshCcw />
                  Convert
                </Button>
              )}
            </div>
          </div>

          <div className="flex w-full gap-2 sm:w-auto">
            <Button variant="outline" onClick={() => onCopyUrl(file.url)} className="flex-1 sm:flex-none">
              <Copy />
              Copy URL
            </Button>
            <Button
              className="flex-1 sm:flex-none"
              onClick={() => {
                const link = document.createElement('a')
                link.href = file.url
                link.download = file.filename || file.name
                link.target = '_blank'
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
              }}
            >
              <Download />
              Download
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
